// src/styles/globalStyles.ts
import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    font-family: 'Inter', sans-serif;
    scroll-behavior: smooth;
    background: ${({ theme }) => theme.background};
    background-image:
      radial-gradient(1200px 600px at 0% 0%, rgba(255,255,255,0.03), transparent 60%),
      radial-gradient(900px 500px at 100% 100%, rgba(255,255,255,0.025), transparent 55%);
    background-attachment: fixed;
    color: ${({ theme }) => theme.text};
    transition: background-color 180ms ease, color 180ms ease;
    overflow-x: hidden;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Reveal animations */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .reveal { opacity: 0; }
  .reveal.is-visible { animation: fadeUp 500ms cubic-bezier(0.22, 1, 0.36, 1) forwards; }

  @media (prefers-reduced-motion: reduce) {
    .reveal.is-visible { animation: none; opacity: 1; }
  }

  /* Subtle noise overlay (monochrome) */
  body::before {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.02'/></svg>");
    background-repeat: repeat;
    z-index: 0;
  }

  /* Smooth theme transitions for common containers */
  main, header, footer, section, nav, article, aside, div {
    transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
  }

  /* Interactive elements baseline */
  a[href], button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"] {
    cursor: pointer;
    transition: color 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
                background-color 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
                border-color 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
                box-shadow 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
                transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  a[href] {
    color: inherit;
    text-decoration: none;
  }
  a[href]:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  /* Focus-visible ring for accessibility */
  a[href]:focus-visible,
  button:focus-visible,
  [role="button"]:focus-visible,
  input[type="button"]:focus-visible,
  input[type="submit"]:focus-visible,
  input[type="reset"]:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(127,127,127,0.35), ${({ theme }) => theme.shadows.md};
  }

  /* Subtle hover elevation for buttons */
  button:hover,
  [role="button"]:hover,
  input[type="button"]:hover,
  input[type="submit"]:hover,
  input[type="reset"]:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  /* Press feedback */
  button:active,
  [role="button"]:active,
  input[type="button"]:active,
  input[type="submit"]:active,
  input[type="reset"]:active {
    transform: translateY(0);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  /* Inputs and textareas smooth focus */
  input, textarea, select {
    transition: background-color 160ms ease,
                color 160ms ease,
                border-color 160ms ease,
                box-shadow 160ms ease;
  }
  input:focus, textarea:focus, select:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px rgba(127,127,127,0.25);
  }

  /* Respect reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    a[href], button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"], input, textarea, select {
      transition: none;
    }
    button:hover, [role="button"]:hover, input[type="button"]:hover, input[type="submit"]:hover, input[type="reset"]:hover { transform: none; }
  }
`;

export default GlobalStyle;
